import * as React from 'react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import { ReactComponent as WeaverLogo } from '../assets/weaver_logo.svg';
import { useNavigate } from 'react-router-dom';
import Lottie from "react-lottie";
import lottieData from '../assets/login-lottie.json';
import request from '../helpers/request';
import { Link } from '@mui/joy';
import { Step, StepLabel, Stepper } from "@mui/material";
import { toast } from "react-toastify";
import { useEffect, useState } from 'react';

const steps = ["Company Name", "User Details", "Password Setup"];

const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: lottieData,
    rendererSettings: {
        preserveAspectRatio: "xMidYMid slice",
    },
};

const Registration = () => {
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        company: "",
        username: "",
        email: "",
        password: "",
        confirmPassword: ""
    });
    const [errors, setErrors] = useState({});
    const [latestAgreement, setLatestAgreement] = useState(null);

    const getLatestAgreement = async () => {
        try {
            const response = await request.get("/api/v1/agreement/");
            setLatestAgreement(response.data);
        } catch (e) {
            console.error("Failed to get the latest agreement:", e);
        }
    };

    useEffect(() => {
        getLatestAgreement();
    }, []);

    const validateFields = () => {
        let newErrors = {};
        if (activeStep === 0 && !formData.company) newErrors.company = "Company name is required";
        if (activeStep === 1) {
            if (!formData.username) newErrors.username = "Username is required";
            if (!formData.email) newErrors.email = "Email is required";
        }
        if (activeStep === 2) {
            if (!formData.password) newErrors.password = "Password is required";
            if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = "Passwords do not match";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleRegistration = async () => {
        try {
            const formattedCompanyName = formData.company.replace(/\s+/g, "-");
            const payload = { ...formData, company_name: formattedCompanyName };

            if (formData.signed) {
                payload.signed_at = new Date().toISOString();
            }

            if (latestAgreement) {
                payload.agreement = latestAgreement.id;
            }

            await request.post('/api/v1/registration/', payload);
            toast.success("Registration successful!");
            navigate("/login");
        } catch (e) {
            if (!e.response) {
                console.error("Network error:", e);
                toast.error("Network error. Please check your connection.");
                return;
            }

            const { status, data } = e.response;

            if (status === 400) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    company: data.company?.[0] || "",
                    username: data.username?.[0] || "",
                    email: data.email?.[0] || "",
                }));

                Object.values(data).flat().forEach((error) => toast.error(error));
                console.error("Validation errors:", data);
                return;
            }

            console.error("Failed to register:", data);
            toast.error("Registration failed. Please try again.");
        }
    };

    const handleNext = () => {
        if (!validateFields()) return;
        if (activeStep === 2) handleRegistration();
        else setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const renderFormField = (field, label, type = "text") => (
        <FormControl
            fullWidth
            error={!!errors[field]}
            sx={{ p: 1.5 }}
        >
            <FormLabel>{label}</FormLabel>
            <Input
                type={type}
                name={field}
                value={formData[field]}
                onChange={handleChange}
                sx={errors[field] ? { borderColor: "red" } : {}}
            />
            {errors[field] && (
                <Typography
                    level="body-sm"
                    color="danger"
                    sx={{ mt: 1, textAlign: "left", width: "100%" }}
                >
                    {errors[field]}
                </Typography>
            )}
        </FormControl>
    );

    const renderRegistrationForm = () => (
        <Box
            sx={{
                width: 400,
                mx: "auto",
                p: 4,
                textAlign: "center",
                borderRadius: 2,
                boxShadow: 3,
                bgcolor: "background.paper",
            }}
        >
            <Typography level="h3" gutterBottom>
                Register
            </Typography>
            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label, index) => (
                    <Step key={index}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>
            <Box sx={{ mt: 4 }}>
                {activeStep === 0 && renderFormField("company", "Company Name")}
                {activeStep === 1 && (
                    <>
                        {renderFormField("username", "Username")}
                        {renderFormField("email", "Email")}
                    </>
                )}
                {activeStep === 2 && (
                    <>
                        {renderFormField("password", "Password", "password")}
                        {renderFormField("confirmPassword", "Confirm Password", "password")}
                        <FormControl
                            fullWidth
                            sx={{
                                p: 1.5,
                                display: "flex",
                                alignItems: "flex-start",
                                flexDirection: "row",
                                alignContent: "center",
                            }}
                        >
                            <Checkbox
                                name="signed"
                                onChange={(event) =>
                                    handleChange({
                                        target: {
                                            name: "signed",
                                            value: event.target.checked ?? false,
                                        },
                                    })
                                }
                            />
                            <FormLabel sx={{ ml: 1 }}>
                                I agree to the{" "}
                                <Link href="/user-agreement" target="_blank" rel="noopener">
                                    User Agreement
                                </Link>
                            </FormLabel>
                        </FormControl>
                    </>
                )}
            </Box>
            <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
                {activeStep > 0 && (
                    <Button onClick={handleBack} variant="soft" color="neutral">
                        Back
                    </Button>
                )}
                <Button onClick={handleNext} variant="solid" color="primary">
                    {activeStep === 2 ? "Register" : "Next"}
                </Button>
            </Box>
            <Box sx={{ mt: 4, textAlign: "center" }}>
                <Typography level="body-sm" sx={{ mt: 2, lineHeight: 1.6 }}>
                    Already have an account?{" "}
                    <Link
                        href="/login"
                        color="primary"
                        fontWeight="lg"
                        underline="none"
                    >
                        Log in
                    </Link>
                </Typography>
            </Box>
        </Box>
    );

    const renderRegistrationSidePanel = () => (
        <Box
            sx={(theme) => ({
                height: "100%",
                position: "fixed",
                right: 0,
                top: 0,
                bottom: 0,
                left: { xs: 0, md: "50vw" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                background:
                    "linear-gradient(225deg, rgba(39,53,101,1) 0%, rgba(83,190,192,1) 100%)",
                padding: "2rem",
            })}
        >
            <Box
                component="img"
                src="/logo_full.png"
                alt="logo"
                sx={{
                    width: 75,
                }}
            />
            <Typography
                level="h2"
                sx={{
                    fontWeight: "bold",
                    mb: 3,
                    color: "#fff",
                    textAlign: "center",
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: "2.5em",
                }}
            >
                DCS
            </Typography>

            <Lottie
                options={defaultOptions}
                height={400}
                width={400}
                sx={{ mb: 3 }}
            />

            <Typography
                variant="subtitle1"
                sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    fontSize: "2em",
                    fontFamily: "'Poppins', sans-serif",
                    mb: 5,
                }}
            >
                Document Compliance System
            </Typography>
        </Box>
    );

    return (
        <>
            <GlobalStyles
                styles={{
                    ":root": {
                        "--Form-maxWidth": "800px",
                        "--Transition-duration": "0.4s",
                    },
                }}
            />
            <Box
                sx={(theme) => ({
                    width: { xs: "100%", md: "50vw" },
                    transition: "width var(--Transition-duration)",
                    transitionDelay: "calc(var(--Transition-duration) + 0.1s)",
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    backdropFilter: "blur(12px)",
                    backgroundColor: "rgba(255 255 255 / 0.2)",
                    [theme.getColorSchemeSelector("dark")]: {
                        backgroundColor: "rgba(19 19 24 / 0.4)",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: "100dvh",
                        width: "100%",
                        px: 2,
                    }}
                >
                    <Box
                        component="header"
                        sx={{ py: 3, display: "flex", justifyContent: "space-between" }}
                    >
                        <WeaverLogo width="200" height="200" />
                    </Box>

                    {renderRegistrationForm()}

                    <Box component="footer" sx={{ py: 3 }}>
                        <Typography level="body-xs" sx={{ textAlign: "center" }}>
                            © Weaversoft.io {new Date().getFullYear()}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {renderRegistrationSidePanel()}
        </>
    );
}

export default Registration
